import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { IoIosStar } from 'react-icons/io'
import { FiPlay } from 'react-icons/fi'
import { movieService } from '../services/movie-service'
import WatchlistButton from './WatchlistButton'
import '../assets/styles/hero.scss'

const API_IMG_LG = 'https://image.tmdb.org/t/p/original'
const ROTATE_MS = 6500

const Hero = ({ type = 'all' }) => {
  const navigate = useNavigate()
  const [slides, setSlides] = useState([])
  const [index, setIndex] = useState(0)
  const timer = useRef(null)

  useEffect(() => {
    const fetcher =
      type === 'tv' ? movieService.getTrendingTv() : movieService.getTrending()
    fetcher.then((items) => {
      const withArt = (items || []).filter((m) => m.backdrop_path).slice(0, 6)
      setSlides(withArt)
    })
  }, [type])

  useEffect(() => {
    if (slides.length < 2) return
    timer.current = setInterval(
      () => setIndex((i) => (i + 1) % slides.length),
      ROTATE_MS
    )
    return () => clearInterval(timer.current)
  }, [slides])

  const goTo = (i) => {
    setIndex(i)
    clearInterval(timer.current)
  }

  const openDetails = (media) =>
    navigate(`/show/${media.id}`, { state: { media, fromSearch: false } })

  // Fallback hero while trending loads or if the API is unavailable
  if (slides.length === 0) {
    return (
      <section className='hero hero-fallback'>
        <div className='hero-glow' aria-hidden='true' />
        <div className='container hero-fallback-inner fade-up'>
          <h1 className='hero-title'>
            Discover, rate &amp; obsess over <br />
            <span className='text-gradient'>every story worth watching.</span>
          </h1>
          <p className='hero-sub'>
            Browse thousands of trending movies and TV shows, watch trailers and
            dive into ratings — all in one beautifully crafted experience.
          </p>
        </div>
      </section>
    )
  }

  const active = slides[index]
  const title = active.title || active.name
  const year = (active.release_date || active.first_air_date || '').substring(0, 4)

  return (
    <section className='hero hero-carousel'>
      {slides.map((s, i) => (
        <div
          key={s.id}
          className={`hero-slide ${i === index ? 'active' : ''}`}
          style={{ backgroundImage: `url(${API_IMG_LG + s.backdrop_path})` }}
          aria-hidden={i !== index}
        />
      ))}
      <div className='hero-veil' />

      <div className='container hero-content'>
        <div className='hero-inner' key={active.id}>
          <span className='hero-badge'>
            <span className='pulse' /> Trending this week
          </span>

          <h1 className='hero-title'>{title}</h1>

          <div className='hero-meta'>
            {typeof active.vote_average === 'number' && (
              <span className='meta-rating'>
                <IoIosStar /> {active.vote_average.toFixed(1)}
              </span>
            )}
            {year && <span>{year}</span>}
            <span className='meta-type'>
              {active.media_type === 'tv' || active.first_air_date
                ? 'TV Series'
                : 'Movie'}
            </span>
          </div>

          <p className='hero-overview'>{active.overview}</p>

          <div className='hero-actions'>
            <button className='btn btn-primary' onClick={() => openDetails(active)}>
              <FiPlay /> View Details
            </button>
            <WatchlistButton media={active} variant='label' />
          </div>
        </div>

        <div className='hero-dots'>
          {slides.map((s, i) => (
            <button
              key={s.id}
              className={`dot ${i === index ? 'active' : ''}`}
              onClick={() => goTo(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  )
}

export default Hero
